/**
 * Guest video invites (spec §8).
 *
 * Wraps the six host-side endpoints and keeps the list the "Gæsteinvitationer" dialog shows. The list refreshes on the
 * PBX_GUEST_INVITE_UPDATED config event while the dialog is open, so the host's, the creator's and a manager's view
 * all stay current. Creating invites is gated on the customer having video and guest calls on and the installed JsSIP
 * being able to carry video.
 *
 * create, revoke and resend throw the API's refusal on to the component, which shows it with guestInviteErrorToast and
 * decides what to do next (keep the modal open, refetch the list). The "all invites" list is the store's own: a 403
 * there turns the manager switch off and hides it for the rest of the session.
 */

import { computed, onScopeDispose, ref, watch, getCurrentScope, type Ref } from 'vue'
import { defineStore } from 'pinia'
import { useApiStore } from '@/stores/Api'
import { useSettingsStore } from '@/stores/Settings'
import { useRealtimeStore } from '@/stores/Realtime'
import { jssipSupportsVideo } from '@/utilities/video/capabilities'
import { guestInviteErrorToast } from '@/utilities/guestInviteErrors'
import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'
import { EmployeeAccessLevel } from '@/TcxApi/Employee'
import type { ErrorResponseDetail } from '@/TcxApi/ErrorResponse'
import type { GuestInviteView, GuestInviteCreateBody, GuestHost, GuestResendResult } from '@/TcxApi/PbxGuestInvite'

export const useGuestInvitesStore = defineStore('guestInvites', () => {
	const api = useApiStore()
	const ss = useSettingsStore()

	const invites = ref<GuestInviteView[]>([])
	const hosts = ref<GuestHost[]>([])
	const loading = ref(false)
	const showAll = ref(false)
	/** The server refused ?all=true (403) - the switch stays hidden for the rest of the session */
	const allRefused = ref(false)

	/** The gate for every entry point and the video call path (spec §8) */
	const canCreateGuestInvites = computed(() =>
		ss.pbxSettings.video?.enabled === true && ss.pbxSettings.guestCalls?.enabled === true && jssipSupportsVideo())

	/** Managers and up may list and revoke every invite at the customer - unless the server said otherwise */
	const canSeeAllInvites = computed(() => ss.accessLevelIsMin(EmployeeAccessLevel.MANAGER) && !allRefused.value)

	/** Load the invites the dialog shows - the caller's, or all of the customer's with the manager switch */
	async function refresh(): Promise<void> {
		loading.value = true

		try {
			const res = await api.get<GuestInviteView[]>(`/pbx/guest/invites${showAll.value ? '?all=true' : ''}`)

			invites.value = res.data
		}
		catch (err) {
			if (showAll.value && (err as Partial<ErrorResponseDetail>).status === 403) {
				logger.info('GuestInvites: The server refused the list of all invites - showing the user\'s own')
				allRefused.value = true
				showAll.value = false
				globalEvents.emit('CommonToast', guestInviteErrorToast('listAll', err))
				await refresh()
				return
			}
			logger.warning(`GuestInvites: Failed to load invites - ${(err as Partial<ErrorResponseDetail>).message ?? String(err)}`)
		}
		finally {
			loading.value = false
		}
	}

	/** The employees the caller may create invites for, self first - drives the host picker */
	async function loadHosts(): Promise<void> {
		const res = await api.get<GuestHost[]>('/pbx/guest/hosts')

		hosts.value = res.data
	}

	/** Create an invite - returns the view with its link; a refusal is thrown on */
	async function create(body: GuestInviteCreateBody): Promise<GuestInviteView> {
		const res = await api.post<GuestInviteView>('/pbx/guest/invites', body)

		return res.data
	}

	/** Revoke an invite - the guest phone and the Redis record go at once; a refusal is thrown on */
	async function revoke(id: string): Promise<void> {
		await api.del(`/pbx/guest/invites/${id}`)
	}

	/** Send the SMS and/or e-mail again, with the same link; a refusal is thrown on */
	async function resend(id: string): Promise<GuestResendResult> {
		const res = await api.post<GuestResendResult>(`/pbx/guest/invites/${id}/resend`)

		return res.data
	}

	/**
	 * Refresh the list on every PBX_GUEST_INVITE_UPDATED while the dialog is open, and only then - the event carries only
	 * the invite id, so the whole list is refetched. The listener is added when open turns true and removed when it turns
	 * false, so a closed dialog neither refetches nor leaks the listener.
	 * @param open Whether the dialog is open
	 */
	function watchWhileOpen(open: Ref<boolean>): void {
		const realtime = useRealtimeStore()
		const onUpdated = () => { void refresh() }
		let subscribed = false
		const subscribe = () => {
			if (subscribed) { return }
			subscribed = true
			realtime.configEvents.on('PBX_GUEST_INVITE_UPDATED', onUpdated)
		}
		const unsubscribe = () => {
			if (!subscribed) { return }
			subscribed = false
			realtime.configEvents.off('PBX_GUEST_INVITE_UPDATED', onUpdated)
		}

		watch(open, isOpen => {
			if (isOpen) {
				void refresh()
				subscribe()
			} else {
				unsubscribe()
			}
		}, { immediate: true })
		// A scope torn down while the dialog is open must not leave the listener on the global config events
		if (getCurrentScope()) { onScopeDispose(unsubscribe) }
	}

	return { invites, hosts, loading, showAll, canCreateGuestInvites, canSeeAllInvites, refresh, loadHosts, create, revoke, resend, watchWhileOpen }
})
